import React, { useState } from 'react';
import { DraftPreviewContent } from './DraftPreviewContent';
import { PlanDiffView } from './PlanDiffView';
import type { PlanDiff } from '../../utils/planDiff';
import './PlanDiffView.css';

type DraftPreviewTab = 'preview' | 'diff';

type DraftPreviewTabsProps = Omit<React.ComponentProps<typeof DraftPreviewContent>, 'yearsToShow' | 'onYearsToShowChange'> & {
    /** The draft against the active plan; null without one, and then there are no tabs */
    diff: PlanDiff | null;
    yearsToShow: number;
    onYearsToShowChange: (years: number) => void;
    onSelectComponent?: (componentKey: string) => void;
    selectedComponentKey?: string | null;
};

/** "Förhandsvisning" and, with an active plan, "Ändringar mot aktiv plan", in the third pane and the preview modal. */
export const DraftPreviewTabs: React.FC<DraftPreviewTabsProps> = ({
    diff,
    yearsToShow,
    onYearsToShowChange,
    onSelectComponent,
    selectedComponentKey,
    ...previewProps
}) => {
    const [tab, setTab] = useState<DraftPreviewTab>('preview');
    const hasActivePlan = !!previewProps.activePlan;

    const select = (next: DraftPreviewTab) => setTab(next);

    return (
        <>
            {hasActivePlan && (
                <div className="draft-preview-tabs" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'preview'}
                        className={`draft-preview-tab ${tab === 'preview' ? 'active' : ''}`}
                        onClick={() => select('preview')}
                    >
                        Förhandsvisning
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'diff'}
                        className={`draft-preview-tab ${tab === 'diff' ? 'active' : ''}`}
                        onClick={() => select('diff')}
                    >
                        Ändringar mot aktiv plan
                        {diff && diff.changeCount > 0 && <span className="draft-preview-tab-count">{diff.changeCount}</span>}
                    </button>
                </div>
            )}
            {tab === 'diff' && hasActivePlan ? (
                diff ? (
                    <PlanDiffView
                        diff={diff}
                        yearsToShow={yearsToShow}
                        onYearsToShowChange={onYearsToShowChange}
                        onSelectComponent={onSelectComponent}
                        selectedComponentKey={selectedComponentKey}
                    />
                ) : (
                    <div className="text-muted small">Beräknar ändringar...</div>
                )
            ) : (
                <DraftPreviewContent
                    {...previewProps}
                    yearsToShow={yearsToShow}
                    onYearsToShowChange={onYearsToShowChange}
                />
            )}
        </>
    );
};
